import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reviewAPI } from '../services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select-new';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { capitalize } from '@/lib/utils';
import { Send, ArrowUpDown } from 'lucide-react';

interface Email {
  id: string;
  from_address: string;
  subject: string;
  category: string;
  received_at: string;
  extracted_entities?: { order_id?: string };
}

export default function ApprovedQueue() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [debouncedOrderId, setDebouncedOrderId] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedLimit, setSelectedLimit] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOrderId(orderIdSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [orderIdSearch]);

  const { data: emails, isLoading } = useQuery({
    queryKey: ['approved-emails'],
    queryFn: async () => {
      const response = await reviewAPI.getApproved();
      return response.data;
    },
  });

  const filteredEmails = emails?.filter((email: Email) => {
    if (categoryFilter && categoryFilter !== 'all' && email.category !== categoryFilter) return false;
    if (debouncedOrderId && !email.extracted_entities?.order_id?.includes(debouncedOrderId)) return false;
    
    if (dateRange !== 'all') {
      const emailDate = new Date(email.received_at);
      const now = new Date();
      
      if (dateRange === 'today') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (emailDate < today) return false;
      } else if (dateRange === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (emailDate < weekAgo) return false;
      } else if (dateRange === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (emailDate < monthAgo) return false;
      } else if (dateRange === 'custom' && startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (emailDate < start || emailDate > end) return false;
      }
    }
    
    return true;
  });

  const sendMutation = useMutation({
    mutationFn: (emailIds: string[]) => 
      Promise.all(emailIds.map(id => reviewAPI.send(id))),
    onSuccess: () => {
      toast.success('Emails sent successfully');
      setSelectedEmails(new Set());
      queryClient.invalidateQueries({ queryKey: ['approved-emails'] });
    },
  });

  const toggleEmail = (id: string) => {
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEmails(newSelected);
  };

  const toggleAll = () => {
    if (selectedEmails.size === filteredEmails?.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(filteredEmails?.map((e: Email) => e.id) || []));
    }
  };

  const handleSendSelected = () => {
    if (selectedEmails.size === 0) return;
    if (window.confirm(`Send ${selectedEmails.size} email(s)?`)) {
      sendMutation.mutate(Array.from(selectedEmails));
    }
  };

  const handleSendSingle = (id: string) => {
    if (window.confirm('Send this email?')) {
      sendMutation.mutate([id]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Apply limit filter
  const limitedEmails = selectedLimit === 'all' 
    ? filteredEmails 
    : filteredEmails?.slice(0, parseInt(selectedLimit));

  // Apply sort order
  const sortedEmails = sortOrder === 'oldest' 
    ? [...(limitedEmails || [])].reverse() 
    : limitedEmails;

  const totalPages = Math.ceil((sortedEmails?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmails = sortedEmails?.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Send Emails</h1>
          <p className="text-muted-foreground mt-2">
            Ready to send ({filteredEmails?.length || 0} emails)
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedEmails.size > 0 && (
            <Button 
              onClick={handleSendSelected} 
              disabled={sendMutation.isPending}
              variant="outline"
              className="min-w-[180px]"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Selected ({selectedEmails.size})
            </Button>
          )}
          {paginatedEmails && paginatedEmails.length > 0 && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={paginatedEmails.every((email: Email) => selectedEmails.has(email.id))}
                onCheckedChange={(checked: boolean) => {
                  if (checked) {
                    const newSelected = new Set(selectedEmails);
                    paginatedEmails.forEach((email: Email) => newSelected.add(email.id));
                    setSelectedEmails(newSelected);
                  } else {
                    const newSelected = new Set(selectedEmails);
                    paginatedEmails.forEach((email: Email) => newSelected.delete(email.id));
                    setSelectedEmails(newSelected);
                  }
                }}
                className="h-4 w-4 rounded border-input"
              />
              <span className="text-muted-foreground">Select All</span>
            </label>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={categoryFilter || 'all'} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="damaged_defective">Damaged Defective</SelectItem>
                <SelectItem value="customer_question">Customer Question</SelectItem>
                <SelectItem value="shipping_issue">Shipping Issue</SelectItem>
                <SelectItem value="refund_request">Refund Request</SelectItem>
                <SelectItem value="order_status">Order Status</SelectItem>
              </SelectContent>
            </Select>

            <Select value={dateRange} onValueChange={(value) => {
              setDateRange(value);
              if (value !== 'custom') {
                setStartDate('');
                setEndDate('');
              }
            }}>
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Last 7 Days</SelectItem>
                <SelectItem value="month">Last 30 Days</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedLimit} onValueChange={setSelectedLimit}>
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="Show All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Show All</SelectItem>
                <SelectItem value="5">Last 5 emails</SelectItem>
                <SelectItem value="10">Last 10 emails</SelectItem>
                <SelectItem value="25">Last 25 emails</SelectItem>
                <SelectItem value="50">Last 50 emails</SelectItem>
                <SelectItem value="100">Last 100 emails</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
              variant="outline"
              size="sm"
              className="h-9"
            >
              <ArrowUpDown className="h-4 w-4 mr-2" />
              {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
            </Button>

            {dateRange === 'custom' && (
              <>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-9 w-auto"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-9 w-auto"
                />
              </>
            )}

            <Input
              type="text"
              value={orderIdSearch}
              onChange={(e) => setOrderIdSearch(e.target.value)}
              placeholder="Search by Order ID"
              className="h-9 w-auto min-w-[200px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <div className="space-y-4">
        {paginatedEmails?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No approved emails ready to send</p>
            </CardContent>
          </Card>
        ) : (
          paginatedEmails?.map((email: Email) => (
            <Card
              key={email.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/review/${email.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedEmails.has(email.id)}
                        onCheckedChange={() => toggleEmail(email.id)}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                      <div>
                        <h3 className="font-semibold">{email.subject}</h3>
                        <p className="text-sm text-muted-foreground">
                          From: {email.from_address.split('<')[0].trim() || email.from_address}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-7">
                      <Badge variant="secondary">
                        {capitalize(email.category || '')}
                      </Badge>
                      {email.extracted_entities?.order_id && (
                        <Badge variant="outline">
                          Order: {email.extracted_entities.order_id}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(email.received_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendSingle(email.id);
                    }}
                    disabled={sendMutation.isPending}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Send
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, sortedEmails?.length || 0)} of {sortedEmails?.length || 0} results
            {selectedLimit !== 'all' && ` (filtered from ${filteredEmails?.length || 0} total)`}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            {/* First page */}
            {currentPage > 3 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                >
                  1
                </Button>
                {currentPage > 4 && <span className="px-2 py-1 text-sm">...</span>}
              </>
            )}
            
            {/* Pages around current */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => page >= currentPage - 2 && page <= currentPage + 2)
              .map(page => (
                <Button
                  key={page}
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className={currentPage === page ? 'bg-muted' : ''}
                >
                  {page}
                </Button>
              ))}
            
            {/* Last page */}
            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && <span className="px-2 py-1 text-sm">...</span>}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </Button>
              </>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
